import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { If } from '../../atoms/if';
import { Box } from '../../atoms/box';
import { Text } from '../../atoms/text';
import { Flex } from '../../atoms/flex';
import { Field } from '../field';
import { Button } from '../../atoms/button';
import { toast } from '../../atoms/toast';
import { Image } from '../../atoms/image';
import { InfoBox } from '../InfoBox';

import { 
  StyledDepositAmountWrapper, 
  StyledDepositAmountTitle, 
  StyledDepositAmount, 
  StyledDepositAmountValue,
  StyledDepositAmountChange,
  StyledQRCodeImageWrapper,
  StyledAccountDetails,
  StyledAccountDetailsSection,
  StyledBanksAccountsWrapper,
  StyledBanksAccountsItem,
  StyledBankName,
  StyledBankType,
  StyledCopyQRCodeText,
  StyledPaymentSectionTitle,
  StyledQRCodeWrapper
} from './styles';

import changeAmountIcon from '../../../images/deposit/change_amount.svg';
import { getBankIcon } from '../../../utils/bankIcons';
import { 
  BANK_ACCOUNT_TYPES_LABELS,
  INGAME_CTA_ACTIONS,
  INGAME_DRAWERS,
} from '../../../utils/constants';
import { formatCurrency } from '../../../utils/currencyFormatting';
import { useTranslation } from '../../../context/translationProvider';
import { useEventTracking } from '../../../context/eventTrackingProvider';

export const DepositPaymentSection = ({
  requestedDepositData,
  currencySymbol,
  handleChange,
  banks,
  isMobile,
  isInGame,
  userId,
}) => {
  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS } = useEventTracking()
  const [showQR, setShowQR] = useState(false);
  const QRCodeRef = useRef(null)
  const { translate } = useTranslation()

  const handleToggleQR = (e) => {
    e.preventDefault();
    setShowQR((prev) => !prev);
  }

  const copy = (e) => {
    e.preventDefault();

    if (isInGame) {
      trackEvent(EVENT_NAMES.INGAME_ACTION_TAKEN, {
        drawer: INGAME_DRAWERS.CONFIRM_PAYMENT,
        cta: INGAME_CTA_ACTIONS.COPY_PIX_CODE,
        site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
        amount: requestedDepositData?.amount,
      }, true)
    }

    try {
      navigator.clipboard.writeText(requestedDepositData?.qrCodeText)
      toast.success(translate('common.copied'))
    } catch (err) {
      toast.error(translate('common.browserFailure'))
    }
  }

    const handleImageLoad = () => {
      if (QRCodeRef.current) {
        QRCodeRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };
  
    const handleImageUnload = () => {
      if (QRCodeRef.current) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  
    useEffect(() => {
      if (showQR && QRCodeRef.current) {
        QRCodeRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
  
      if (!showQR) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, [showQR]);

  useEffect(() => {
    trackEvent(EVENT_NAMES.PIX_REVIEW_LOADED, {
      amount: requestedDepositData?.amount,
      user_id: userId,
      ...(isInGame && { site_location: EVENT_LOCATIONS.INGAME_NAVBAR }),
    }, true)
    trackEvent(EVENT_NAMES.SCREEN_LOADED, {
      screen_name: EVENT_LOCATIONS.PIX,
    }, true) 
  }, []);

  return (
    <>
      <StyledPaymentSectionTitle>
        {translate('payments.deposit.paymentTitle')}
      </StyledPaymentSectionTitle>

      <StyledDepositAmountWrapper showQR={showQR}>
        <StyledDepositAmountTitle>
          {translate('payments.deposit.amountToBepaid')}
        </StyledDepositAmountTitle>

        <StyledDepositAmount>
          <StyledDepositAmountValue>
            {currencySymbol}
            {' '}
            {formatCurrency(requestedDepositData?.amount)}
          </StyledDepositAmountValue>

          <If 
            condition={!!handleChange}
            render={() => (
              <StyledDepositAmountChange onClick={handleChange}>
                <img src={changeAmountIcon} alt="Change amount Icon" />
                <span>{translate('payments.deposit.changeButton')}</span>
              </StyledDepositAmountChange>
            )}
          />
        </StyledDepositAmount>

        <If 
          condition={banks?.length <= 1}
          render={() => (
            <StyledAccountDetails>
              <StyledAccountDetailsSection>
                <span>{translate('payments.deposit.bank')}:</span>
                <span>{banks[0]?.bank?.name}</span>
              </StyledAccountDetailsSection>
              <StyledAccountDetailsSection>
                <span>{translate('payments.deposit.agency')}:</span>
                <span>{banks[0]?.bankBranch}</span>
              </StyledAccountDetailsSection>  
              <StyledAccountDetailsSection>
                <span>{translate('payments.deposit.account')}:</span>
                <span>{banks[0]?.accountNumber}</span>
              </StyledAccountDetailsSection>  
            </StyledAccountDetails>
          )}
        />

        <InfoBox
          theme={'outlined'}
          title={translate('payments.deposit.infoBoxTitle')}
          content={translate('payments.deposit.infoBoxContent')}
          extraContent={
            banks?.length > 1 ? (
              <StyledBanksAccountsWrapper>
                <span>
                  {translate('payments.deposit.yourRegisteredAccounts')}
                </span>

                {banks?.map((bank, index) => (
                  <StyledBanksAccountsItem key={index}>
                    <Image 
                      url={getBankIcon(bank?.bank?.code)}
                      height="20px"
                      width="20px"
                      style={{
                        filter: 'grayscale(100%)',
                      }}
                    />

                    <div>
                      <StyledBankName>{bank?.bank?.name}</StyledBankName>
                      <StyledBankType>
                        {translate(
                          BANK_ACCOUNT_TYPES_LABELS[bank?.accountType]
                        )}
                      </StyledBankType>
                    </div>
                  </StyledBanksAccountsItem>
                ))}
              </StyledBanksAccountsWrapper>
            ) : null
          }
        />

        <StyledQRCodeWrapper>
          <Box 
            marginBottom="1rem"
          >
            <If
              condition={!isMobile}
              render={() => (
                <StyledQRCodeImageWrapper>
                  <h3>{translate('payments.deposit.scanQRCode')}</h3>

                  <Image 
                    url={requestedDepositData?.qrCodeImage} 
                    width="40%" 
                  />
                </StyledQRCodeImageWrapper>
              )}
            />

            <StyledCopyQRCodeText>
              {translate('payments.deposit.copyLink')}
            </StyledCopyQRCodeText>

            <Flex gap="8px" maxWidth="100%">
              <Box>
                <Field
                  disabled
                  forceValue={requestedDepositData?.qrCodeText}
                  noBottomMargin
                />
              </Box>
              <Box>
                <Button 
                  width="100%" 
                  minWidth="auto" 
                  height="84px"
                  onClick={copy}
                >	
                  {translate('common.copy')}
                </Button>
              </Box>
            </Flex>
          </Box>

          <If
            condition={isMobile}
            render={() => (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  justify="center"
                  style={{ 
                    position: 'relative' 
                  }}
                >
                  <Button
                    type="text"
                    onClick={handleToggleQR}
                  >
                    <Text
                      small="yes"
                      textDecoration="underline"
                    >
                      {showQR
                        ? translate('payments.deposit.hideQR')
                        : translate('payments.deposit.showQR')
                      }
                    </Text>
                  </Button>
                </Box>

                <If 
                  condition={showQR}
                  render={() => (
                    <StyledQRCodeImageWrapper
                      ref={QRCodeRef} 
                      onLoad={handleImageLoad} 
                      onUnload={handleImageUnload}
                    >
                      <Image
                        url={requestedDepositData?.qrCodeImage}
                        width="100%"
                      />
                    </StyledQRCodeImageWrapper>
                  )}
                />
              </>
            )}
          />
        </StyledQRCodeWrapper>
      </StyledDepositAmountWrapper>
    </>
  );
};

DepositPaymentSection.propTypes = {
  translate: PropTypes.func.isRequired,
  requestedDepositData: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  handleChange: PropTypes.func,
  banks: PropTypes.array,
  isMobile: PropTypes.bool,
  handleToggleQR: PropTypes.func.isRequired, 
  userId: PropTypes.string.isRequired,
  isInGame: PropTypes.bool,
};